import React from 'react';
import { motion } from 'framer-motion';
import { Lightning, Gauge, Rocket, CheckCircle, Star } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const WebsiteSpeedFeatures = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: <Lightning className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.performanceAnalysis.title'),
      description: t('websiteSpeedFeatures.performanceAnalysis.description')
    },
    {
      icon: <Gauge className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.codeOptimization.title'),
      description: t('websiteSpeedFeatures.codeOptimization.description')
    },
    {
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.cachingCdn.title'),
      description: t('websiteSpeedFeatures.cachingCdn.description')
    },
    {
      icon: <Lightning className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.coreWebVitals.title'),
      description: t('websiteSpeedFeatures.coreWebVitals.description')
    },
    {
      icon: <CheckCircle className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.databaseOptimization.title'),
      description: t('websiteSpeedFeatures.databaseOptimization.description')
    },
    {
      icon: <Star className="w-8 h-8" weight="thin" />,
      title: t('websiteSpeedFeatures.monitoring.title'),
      description: t('websiteSpeedFeatures.monitoring.description')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Wat onze <span className="text-blue-600">snelheidsoptimalisatie</span> omvat
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van performance analyse tot code optimalisatie. We maken je website razendsnel 
            zodat bezoekers blijven en je Google ranking verbetert.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {features.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="bg-white rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
                      <div className="bg-blue-900 rounded-3xl p-12 text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6">
                              <Star className="w-8 h-8 text-blue-900" weight="fill" />
            </div>
            <h3 className="text-3xl font-bold mb-4">Klaar om je website te versnellen?</h3>
                          <p className="text-blue-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              We maken je website razendsnel zodat bezoekers blijven en je conversies stijgen. 
              Van analyse tot implementatie - we optimaliseren elke pixel voor snelheid.
            </p>
            <Link to="/start-your-project" className="inline-flex items-center bg-white text-blue-900 px-10 py-4 rounded-2xl font-semibold hover:bg-blue-50 transition-all duration-300">
              <span>Start je speed project</span>
              <Rocket className="w-5 h-5 ml-2" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebsiteSpeedFeatures;
